import {
  registerApplication,
  start,
  addErrorHandler,
  type AppError,
} from "single-spa";
import type { ShellHandle } from "@observe-tech/mfe-shell-api";
import type { MfeErrorApi } from "../shell-handle/mfeError";

const SHELL_OWNED_PREFIXES = [
  "/login",
  "/logout",
  "/welcome",
  "/access-denied",
  "/not-found",
  "/test-mfe",
];
const SHELL_OWNED_EXACT = new Set(["/", "/login/callback"]);
const TIDE_PREFIX = "/tide";
const AKVA_PREFIX = "/akva-analytics";
const MARLIN_V2_PREFIX = "/marlin-v2";
const SUBMERGED_PREFIX = "/submerged";

export function isShellOwnedPath(pathname: string): boolean {
  if (SHELL_OWNED_EXACT.has(pathname)) return true;
  return SHELL_OWNED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

function getMfeContainer(): HTMLElement {
  let el = document.getElementById("single-spa-app-container");
  if (!el) {
    el = document.createElement("div");
    el.id = "single-spa-app-container";
    document.body.appendChild(el);
  }
  return el;
}

let registered = false;
let started = false;
let errorHandlerRegistered = false;

export function attachMfeErrorHandler(mfeError: MfeErrorApi) {
  if (errorHandlerRegistered) return;
  errorHandlerRegistered = true;
  addErrorHandler((err: AppError) => {
    const appName =
      typeof err.appOrParcelName === "string"
        ? err.appOrParcelName
        : "unknown-mfe";
    console.error("[shell] MFE error", appName, err);
    mfeError.set(appName, err);
  });
}

export function registerMfes(handle: ShellHandle) {
  if (registered) return;
  registered = true;

  const customProps = () => ({
    shellHandle: handle,
    domElement: getMfeContainer(),
    domElementGetter: () => getMfeContainer(),
  });

  const tideUrl = import.meta.env.VITE_TIDE_MFE_URL;
  if (tideUrl) {
    registerApplication({
      name: "@observe/tide",
      app: () => import(/* @vite-ignore */ tideUrl),
      activeWhen: (loc) => loc.pathname.startsWith(TIDE_PREFIX),
      customProps,
    });
  }

  const akvaUrl = import.meta.env.VITE_AKVA_MFE_URL;
  if (akvaUrl) {
    registerApplication({
      name: "@observe/akva",
      app: () => import(/* @vite-ignore */ akvaUrl),
      activeWhen: (loc) => loc.pathname.startsWith(AKVA_PREFIX),
      customProps,
    });
  }

  const marlinV2Url = import.meta.env.VITE_MARLIN_V2_MFE_URL;
  if (marlinV2Url) {
    registerApplication({
      name: "@observe/marlin-v2",
      app: () => import(/* @vite-ignore */ marlinV2Url),
      activeWhen: (loc) => loc.pathname.startsWith(MARLIN_V2_PREFIX),
      customProps,
    });
  }

  const submergedUrl = import.meta.env.VITE_SUBMERGED_MFE_URL;
  if (submergedUrl) {
    registerApplication({
      name: "@observe/submerged",
      app: () => import(/* @vite-ignore */ submergedUrl),
      activeWhen: (loc) => loc.pathname.startsWith(SUBMERGED_PREFIX),
      customProps,
    });
  }
}

export function startSingleSpa() {
  if (started) return;
  started = true;
  start({ urlRerouteOnly: true });
}
